import clsx from 'clsx'
import { ChevronRight, Folder } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { ExternalFileItem } from '@/user_files/external-storage/schemas'

interface ExternalFolderListProps {
  error: string | null
  folders: ExternalFileItem[]
  loading: boolean
  onDrillIn: (folder: ExternalFileItem) => void
  onSelectFolder: (folderId: string) => void
  selectedFolderId: string | null
  unavailableFolderIds?: ReadonlySet<string>
}

export function ExternalFolderList({ error, folders, loading, onDrillIn, onSelectFolder, selectedFolderId, unavailableFolderIds }: ExternalFolderListProps) {
  return (
    <div className="h-[320px] overflow-y-auto">
      {loading && (
        <div className="flex h-full items-center justify-center">
          <CircularProgressContinuousSized size={24} thickness={7} />
        </div>
      )}
      {!loading && error && <p className="flex h-full items-center justify-center px-4 text-center text-sm text-red-600">{error}</p>}
      {!loading && !error && folders.length === 0 && (
        <p className="flex h-full items-center justify-center px-4 text-center text-sm text-brand-neutral-500">No folders found</p>
      )}
      {!loading &&
        !error &&
        folders.map((folder) => {
          const isUnavailable = unavailableFolderIds?.has(folder.id) ?? false
          return (
            <div
              key={folder.id}
              className={clsx('flex h-12 items-center border-t border-brand-neutral-200', isUnavailable ? 'bg-brand-neutral-50' : 'hover:bg-brand-neutral-100')}
            >
              <button
                type="button"
                disabled={isUnavailable}
                onClick={() => onSelectFolder(folder.id)}
                className={clsx('flex h-full min-w-0 flex-1 items-center gap-3 px-4 text-left', isUnavailable && 'cursor-not-allowed')}
                aria-label={isUnavailable ? `${folder.name} is synced to another matter` : `Select ${folder.name}`}
              >
                <span
                  className={clsx(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border bg-white',
                    isUnavailable ? 'border-brand-neutral-200' : 'border-brand-neutral-300'
                  )}
                  aria-hidden="true"
                >
                  {selectedFolderId === folder.id && !isUnavailable && <span className="h-2.5 w-2.5 rounded-full bg-brand-500" />}
                </span>
                <Folder className={clsx('h-6 w-6 shrink-0', isUnavailable ? 'text-brand-neutral-400' : 'text-brand-neutral-950')} />
                <span
                  className={clsx('min-w-0 flex-1 truncate text-sm', isUnavailable ? 'text-brand-neutral-500' : 'text-brand-neutral-950')}
                  title={folder.name}
                >
                  {folder.name}
                </span>
                {isUnavailable && <span className="shrink-0 rounded bg-brand-neutral-100 px-2 py-0.5 text-xs font-medium text-brand-neutral-600">In use</span>}
              </button>
              <button
                type="button"
                disabled={isUnavailable}
                onClick={() => onDrillIn(folder)}
                className={clsx(
                  'mr-4 flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                  isUnavailable ? 'cursor-not-allowed' : 'hover:bg-brand-neutral-200'
                )}
                aria-label={isUnavailable ? `${folder.name} cannot be opened because it is synced to another matter` : `Open ${folder.name}`}
              >
                <ChevronRight className={clsx('h-5 w-5', isUnavailable ? 'text-brand-neutral-300' : 'text-brand-neutral-600')} />
              </button>
            </div>
          )
        })}
    </div>
  )
}
